export type GroupMode = 'detail' | 'summary';
export type SortBy = 'time' | 'user' | 'bin' | 'po_job';

export interface Recipient {
  user_id: number;
  name: string;
  email: string;
  assigned?: boolean;
}

export interface LocationOption {
  ovid: number;
  name: string;
  customer_name: string | null;
}

export interface UsageReportSchedule {
  id: number;
  name: string;
  ovid: number;
  location_name: string | null;
  customer_name: string | null;
  active_flag: number;
  timezone: string;
  run_time: string;
  run_days: number[];
  range_start_time: string;
  range_end_time: string;
  group_mode: GroupMode;
  sort_by: SortBy;
  replenish_enabled: number;
  skip_if_empty: number;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string | null;
  recipients: Recipient[];
}

export interface UsageReportFormValues {
  name: string;
  ovid: number | '';
  active_flag: number;
  timezone: string;
  run_time: string;
  run_days: number[];
  range_start_time: string;
  range_end_time: string;
  group_mode: GroupMode;
  sort_by: SortBy;
  replenish_enabled: number;
  skip_if_empty: number;
  recipients: Recipient[];
}

export type UsageSource = 'abatix' | 'non_abatix';

export const SOURCE_LABELS: Record<UsageSource, string> = {
  abatix: 'ABATIX',
  non_abatix: 'Non-ABATIX',
};

export interface PreviewRow {
  source: UsageSource;
  PID: number;
  product_code: string;
  description: string;
  uom: string;
  quantity: number;
  transaction_count: number;
  user_name: string;
  bin_name: string;
  po_job_number: string;
  first_at: string;
  last_at: string;
  tids: number[];
  replenishable: boolean;
}

export interface PreviewResult {
  range_start: string;
  range_end: string;
  timezone: string;
  total_quantity: number;
  rows: PreviewRow[];
}

export interface UsageReportRun {
  id: number;
  trigger_type: 'schedule' | 'manual';
  triggered_by_name: string | null;
  range_start: string;
  range_end: string;
  row_count: number;
  recipient_count: number;
  status: string;
  error_message: string | null;
  created_at: string;
  replenish_lines: number;
  replenished_lines: number;
}

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Schedules run and display in UTC, like the rest of AIMS. */
export const REPORT_TIMEZONE = 'UTC';

export const GROUP_MODE_LABELS: Record<GroupMode, string> = {
  detail: 'Every transaction line',
  summary: 'Item totals by user / bin / PO-Job',
};

export const SORT_LABELS: Record<SortBy, string> = {
  time: 'Time of transaction',
  user: 'User',
  bin: 'Bin',
  po_job: 'PO/Job No.',
};

export const formatClock = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`;
};

export const describeDays = (days: number[]): string => {
  const sorted = [...days].sort();
  if (sorted.length === 7) return 'Every day';
  if (sorted.join(',') === '1,2,3,4,5') return 'Weekdays';
  if (sorted.join(',') === '0,6') return 'Weekends';
  return sorted.map((d) => DAY_LABELS[d]).join(', ');
};

type RangeFields = Pick<UsageReportFormValues, 'run_time' | 'range_start_time' | 'range_end_time'>;

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Same rule as the API: each run pulls the most recent "from -> to" period that
 * has ended by the run time, crossing midnight when "from" is not before "to".
 */
const rangeDays = (v: RangeFields) => {
  const end = minutesOf(v.range_end_time) <= minutesOf(v.run_time) ? 0 : 1;
  const start = end + (minutesOf(v.range_start_time) < minutesOf(v.range_end_time) ? 0 : 1);
  return { start, end };
};

const rangeHours = (v: RangeFields) => {
  const diff = (minutesOf(v.range_end_time) - minutesOf(v.range_start_time) + 24 * 60) % (24 * 60) || 24 * 60;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m ? `${h}h ${m}m` : `${h} hours`;
};

const dayWord = (daysBack: number) => (daysBack === 0 ? 'today' : daysBack === 1 ? 'yesterday' : `${daysBack} days ago`);

/** e.g. "The 6:00 AM email covers yesterday 12:00 AM → today 12:00 AM (24 hours)." */
export const describeRange = (v: RangeFields): string => {
  const days = rangeDays(v);
  return `The ${formatClock(v.run_time)} email covers ${dayWord(days.start)} ${formatClock(v.range_start_time)} → `
    + `${dayWord(days.end)} ${formatClock(v.range_end_time)} (${rangeHours(v)}).`;
};

/** Short form for the schedules table, e.g. "12:00 AM → 12:00 AM (24 hours)". */
export const describeRangeShort = (v: RangeFields): string =>
  `${formatClock(v.range_start_time)} → ${formatClock(v.range_end_time)} (${rangeHours(v)})`;
